// src/modals/ResaModal.jsx — modale de saisie/édition d'une réservation.
// Champs : check-in, voyageur, plateforme (chips), nuits, montant, ménage,
// taxe séjour, notes. Sauve via onSave (parent gère la persistance).
// Date check-in obligatoire pour activer le bouton Enregistrer.
//
// MODE V2 (prop `v2` truthy) — logements de source de facturation 'v2' (sync
// Beds24). Décision Dimitri : toute correction (montant ET ménage) passe par
// CETTE modale (le ✏️ existant), comme en V1 — fini la cellule inline doublon.
// En V2 :
//   - Éditables : montant net hôte + ménage uniquement.
//   - Lecture seule (« issu de Beds24 — non modifiable ») : voyageur, plateforme,
//     nuits, taxe, notes.
//   - Affiche le net proprio live (D3) et un avertissement ambre non bloquant si
//     montant < ménage (D4).
//   - Sans code stable (airbnbCode || bookingRef) : montant+ménage AUSSI en
//     lecture seule + message « non corrigeable ».
//   - onSave renvoie { id, airbnbCode, bookingRef, montant, menage } — le parent
//     route vers les overrides (jamais le KV reservations[]).
// MODE V1 (prop `v2` absent/false) : comportement strictement inchangé.
//
// MODE MANUEL (prop `manualMode` truthy — Lot 4 V2-create-manual) : création/
// édition d'une résa MANUELLE sur un logement/chambre V2. TOUS les champs sont
// éditables COMME EN V1 (voyageur, dates, nuits, montant, ménage, taxe, notes)
// SAUF la plateforme, VERROUILLÉE sur 'manuel' (chip « Manuel » affiché
// sélectionné/désactivé). onSave renvoie l'objet COMPLET (shape V1) — le parent
// route vers createResaV2 (création) ou saveResa (édition d'une manuelle).

import { useState } from "react"
import { AlertTriangle } from "lucide-react"
import { DS } from "../lib/ui"
import { PL_LABEL, PL_COLOR } from "../lib/domain-constants"

import useEchapPourFermer from "../lib/useEchapPourFermer"
import { commissionLigneFacture } from "../lib/commission-lignes"
// Net proprio prévisionnel d'après la saisie en cours (pure, pas d'I/O).
// net = montant − commissionFBZ − menage. La commission vient de LA fonction de
// la facture (commission-lignes, lot A4 du 30/10). Sous-loc taux=0 → net =
// montant − menage. TAUX INCONNU (absent, vide) → `undefined` : l'aperçu
// affiche « — », jamais un net calculé à 0 % en silence.
export function computeNetProprio(montant, menage, taux) {
  const m = Number(montant)
  if (!Number.isFinite(m)) return null
  if (taux == null || taux === "" || !Number.isFinite(Number(taux))) return undefined
  const men = Number(menage) || 0
  const { comm } = commissionLigneFacture({ montant: m, menage: men }, { baremeSrc: null, tauxFixe: Number(taux) })
  return Math.round((m - comm - men) * 100) / 100
}

const fmtMoney2 = (n) => (Number(n) || 0).toLocaleString("fr-FR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })

export default function ResaModal({ lid, logNom, resa, onSave, onClose, v2 = false, manualMode = false, taux = null, hasStableCode = true }) {
  // Échap ferme — consigne 2026-08-14, hook partagé (pile anti-double-fermeture).
  useEchapPourFermer(onClose)

  // En manualMode, la plateforme est TOUJOURS 'manuel' (verrouillée).
  const defaultPlateforme = manualMode ? "manuel" : "airbnb"
  const empty = { id: null, voyageur: "", plateforme: defaultPlateforme, checkIn: (() => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}` })(), nuits: "", montant: "", menage: "", taxe: "", notes: "" }
  const [form, setForm] = useState(resa ? {
    ...resa, checkIn: resa.checkIn || resa.date || "",
    plateforme: manualMode ? "manuel" : (resa.plateforme || "airbnb"),
    nuits: String(resa.nuits || ""), montant: String(resa.montant || ""),
    menage: String(resa.menage || ""), taxe: String(resa.taxe || ""),
  } : empty)
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }))
  // En V2, montant/ménage ne sont éditables QUE si la résa a un code stable.
  const v2EditableMoney = v2 && hasStableCode
  const lbl = { fontSize:11,color:DS.textMuted,marginBottom:4,fontWeight:600,textTransform:"uppercase",letterSpacing:.5 }
  const inp = (k, type = "text", placeholder = "", readOnly = false) => (
    <input type={type} value={form[k]} onChange={e => set(k, e.target.value)} placeholder={placeholder}
      readOnly={readOnly} disabled={readOnly}
      style={{border:`0.5px solid ${readOnly?DS.border:DS.borderHover}`,borderRadius:DS.radiusControl,padding:"9px 12px",width:"100%",fontSize:13,outline:"none",fontFamily:DS.fontBody,background:readOnly?DS.pageBg:DS.card,color:readOnly?DS.textMuted:"inherit",cursor:readOnly?"not-allowed":"text"}} />
  )
  // Note de repli « issu de Beds24 — non modifiable » sous un champ verrouillé V2.
  const beds24Note = () => v2 ? <div style={{fontSize:10,color:DS.textMuted,marginTop:3}}>issu de Beds24 — non modifiable</div> : null

  const handleSave = () => {
    if (v2) {
      // Mode V2 : on ne renvoie QUE l'identité + les champs corrigeables, en
      // CHAÎNES BRUTES (un champ vidé '' = demande de reset au brut Beds24 —
      // surtout pas 0). Le parent (saveResaV2) route vers saveMontantOverride /
      // saveMenageOverride qui interprètent '' comme suppression de l'override.
      onSave({
        id: form.id,
        airbnbCode: resa ? resa.airbnbCode : undefined,
        bookingRef: resa ? resa.bookingRef : undefined,
        montant: form.montant,
        menage: form.menage,
      })
      return
    }
    onSave({
      ...form, checkIn: form.checkIn, date: form.checkIn,
      nuits: parseFloat(form.nuits) || 0, montant: parseFloat(form.montant) || 0,
      menage: parseFloat(form.menage) || 0, taxe: parseFloat(form.taxe) || 0,
    })
  }

  // Aperçu net proprio live (D3/D4) — uniquement en V2.
  const parsedMontant = Number(String(form.montant).replace(",", "."))
  const parsedMenage = Number(String(form.menage).replace(",", ".")) || 0
  const hasParsed = Number.isFinite(parsedMontant)
  const netProprio = hasParsed ? computeNetProprio(parsedMontant, parsedMenage, taux) : null
  const sousLeMenage = hasParsed && parsedMontant < parsedMenage

  const labelMontant = v2 ? "Montant net hôte (€) — avant commission FBZ et ménage" : "Montant reçu (€)"

  return (
    <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,.55)",zIndex:999,display:"flex",alignItems:"flex-end",justifyContent:"center"}}>
      <div className="fbz-modal-sheet" style={{background:DS.card,borderRadius:"20px 20px 0 0",padding:24,width:"100%",maxWidth:480,maxHeight:"90vh",overflowY:"auto",fontFamily:DS.fontBody}}>
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:14}}>
          <div>
            <div style={{fontFamily:DS.fontDisplay,fontWeight:600,color:DS.marineDark,fontSize:17}}>{form.id ? "Modifier" : "Ajouter"} une réservation</div>
            <div style={{fontSize:11,color:DS.textMuted,marginTop:2}}>{logNom}</div>
          </div>
          <button onClick={onClose} style={{background:"none",border:"none",fontSize:24,cursor:"pointer",color:DS.textMuted,lineHeight:1}}>×</button>
        </div>

        <div style={{marginBottom:12}}><div style={lbl}>Date check-in <span style={{color:DS.danger,fontSize:10}}>*</span></div>{inp("checkIn","date","",v2)}{beds24Note()}</div>
        <div style={{marginBottom:12}}><div style={lbl}>Voyageur</div>{inp("voyageur","text","Nom du voyageur",v2)}{beds24Note()}</div>
        <div style={{marginBottom:12}}>
          <div style={lbl}>Plateforme</div>
          {manualMode ? (
            // MODE MANUEL : plateforme VERROUILLÉE sur 'manuel' (un seul chip
            // sélectionné/désactivé). Pas de choix Airbnb/Booking/Direct ici.
            <div style={{display:"flex",gap:8}}>
              <button disabled
                style={{flex:1,padding:"9px 4px",border:`2px solid ${PL_COLOR.manuel||"#8a6d3b"}`,borderRadius:10,background:(PL_COLOR.manuel||"#8a6d3b")+"15",color:PL_COLOR.manuel||"#8a6d3b",fontWeight:700,fontSize:12,cursor:"not-allowed",fontFamily:DS.fontBody}}>
                {PL_LABEL.manuel||"Manuel"}
              </button>
            </div>
          ) : (
            <div style={{display:"flex",gap:8}}>
              {["airbnb","booking","direct"].map(pl => (
                <button key={pl} onClick={() => { if (!v2) set("plateforme", pl) }} disabled={v2}
                  style={{flex:1,padding:"9px 4px",border:`1.5px solid ${form.plateforme===pl?PL_COLOR[pl]:DS.border}`,borderRadius:DS.radiusControl,background:form.plateforme===pl?PL_COLOR[pl]+"15":DS.card,color:form.plateforme===pl?PL_COLOR[pl]:DS.textMuted,fontWeight:700,fontSize:12,cursor:v2?"not-allowed":"pointer",fontFamily:DS.fontBody,opacity:v2&&form.plateforme!==pl?0.5:1}}>
                  {PL_LABEL[pl]}
                </button>
              ))}
            </div>
          )}
          {beds24Note()}
        </div>
        <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10,marginBottom:12}}>
          <div><div style={lbl}>Nuits</div>{inp("nuits","number","0",v2)}{beds24Note()}</div>
          <div><div style={lbl}>{labelMontant}</div>{inp("montant","number","0.00",v2 && !v2EditableMoney)}</div>
          <div><div style={lbl}>Ménage (€)</div>{inp("menage","number","0.00",v2 && !v2EditableMoney)}</div>
          <div>
            <div style={lbl}>
              Taxe séjour {form.plateforme === "direct" ? <span style={{color:DS.danger,fontSize:10}}>Sur facture</span> : <span style={{color:DS.textMuted,fontSize:10}}>Info</span>}
            </div>
            {inp("taxe","number","0.00",v2)}
          </div>
        </div>
        {v2 && !hasStableCode && (
          <div style={{marginBottom:12,padding:"9px 12px",background:DS.cielSoft,border:`1px solid ${DS.ciel}55`,borderRadius:8,fontSize:12,color:DS.marineDeep}}>
            Réservation sans code stable (Airbnb/Booking) — non corrigeable.
          </div>
        )}
        {v2EditableMoney && netProprio !== null && (
          <div style={{marginBottom:8,fontSize:12,color:DS.textSecondary}}>
            → net proprio : <strong>{netProprio === undefined ? "—" : `${fmtMoney2(netProprio)} €`}</strong>
          </div>
        )}
        {v2EditableMoney && sousLeMenage && (
          <div style={{display:"flex",gap:7,alignItems:"flex-start",marginBottom:12,padding:"9px 12px",background:DS.warningBg,border:`1px solid ${DS.warning}55`,borderRadius:8,fontSize:12,color:DS.warningText}}>
            <AlertTriangle size={14} strokeWidth={2.2} style={{flexShrink:0,marginTop:1}}/><span>sous le ménage : commission nulle, net proprio négatif</span>
          </div>
        )}
        {!v2 && (
          <div style={{marginBottom:20}}><div style={lbl}>Notes</div>{inp("notes","text","Optionnel")}</div>
        )}
        {v2 && (
          <div style={{marginBottom:20}}><div style={lbl}>Notes</div>{inp("notes","text","Optionnel",true)}{beds24Note()}</div>
        )}
        <div style={{display:"flex",gap:8}}>
          <button className="fbz-press" onClick={onClose} style={{flex:1,padding:12,background:DS.card,color:DS.marine,border:`0.5px solid ${DS.borderHover}`,borderRadius:DS.radiusControl,fontWeight:600,cursor:"pointer",fontFamily:DS.fontBody}}>Annuler</button>
          <button className="fbz-press" onClick={handleSave} disabled={!form.checkIn || (v2 && !v2EditableMoney)}
            style={{flex:2,padding:12,background:DS.corail,color:"#fff",border:"none",borderRadius:DS.radiusControl,fontWeight:700,cursor:(form.checkIn && !(v2 && !v2EditableMoney))?"pointer":"not-allowed",fontFamily:DS.fontBody,opacity:(form.checkIn && !(v2 && !v2EditableMoney))?1:0.5}}>Enregistrer</button>
        </div>
      </div>
    </div>
  )
}
